import React, { useEffect } from "react";

interface PaginationComponentProps {
  itemsCount: number;
  itemsPerPage: number;
  setItemsPerPage: (page: number) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  alwaysShown?: boolean;
  resetPageOnItemsPerPageChange?: boolean;
}

const TablePaginationComponent: React.FC<PaginationComponentProps> = ({
  itemsCount = 0,
  itemsPerPage = 10,
  currentPage = 1,
  setCurrentPage,
  alwaysShown = true,
  setItemsPerPage,
  resetPageOnItemsPerPageChange = true
}) => {
  const pagesCount = Math.ceil(itemsCount / itemsPerPage);
  const isPaginationShown = alwaysShown || pagesCount > 1;
  const isCurrentPageFirst = currentPage === 1;
  const isCurrentPageLast = pagesCount === 0 || currentPage === pagesCount;

  const changePage = (number: number) => {
    if (currentPage === number || number < 1 || (pagesCount > 0 && number > pagesCount)) return;
    setCurrentPage(number);
  };

  const setLastPageAsCurrent = () => {
    const nextPage = pagesCount || 1;
    if (currentPage > pagesCount && currentPage !== nextPage) {
      setCurrentPage(nextPage);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setLastPageAsCurrent, [pagesCount, currentPage]);

  let isPageNumberOutOfRange = false;

  const pageNumbers = Array.from({ length: pagesCount }, (_, index) => {
    const pageNumber = index + 1;
    const isPageNumberFirst = pageNumber === 1;
    const isPageNumberLast = pageNumber === pagesCount;
    const isCurrentPageWithinTwoPageNumbers = Math.abs(pageNumber - currentPage) <= 2;

    if (isPageNumberFirst || isPageNumberLast || isCurrentPageWithinTwoPageNumbers) {
      isPageNumberOutOfRange = false;
      const isActive = pageNumber === currentPage;
      return (
        <button
          key={pageNumber}
          onClick={() => changePage(pageNumber)}
          style={{
            minWidth: 34,
            height: 34,
            padding: '0 4px',
            border: isActive ? 'none' : '1px solid #e5e7eb',
            borderRadius: 8,
            background: isActive ? '#486bad' : '#fff',
            color: isActive ? '#fff' : '#374151',
            fontWeight: isActive ? 700 : 400,
            fontSize: 13,
            cursor: isActive ? 'default' : 'pointer',
            transition: 'background 0.15s, border-color 0.15s',
            outline: 'none',
          }}
          onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
          onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
        >
          {pageNumber}
        </button>
      );
    }

    if (!isPageNumberOutOfRange) {
      isPageNumberOutOfRange = true;
      return (
        <span key={`ellipsis-${index}`} style={{ minWidth: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#9ca3af' }}>
          &hellip;
        </span>
      );
    }

    return null;
  });

  const navBtnStyle = (disabled: boolean): React.CSSProperties => ({
    width: 34,
    height: 34,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: disabled ? '#f9fafb' : '#fff',
    color: disabled ? '#d1d5db' : '#374151',
    fontSize: 14,
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    transition: 'background 0.15s',
    flexShrink: 0,
  });

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0 4px', flexWrap: 'wrap', gap: 10 }}>
      {isPaginationShown && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            onClick={() => changePage(currentPage - 1)}
            disabled={isCurrentPageFirst}
            style={navBtnStyle(isCurrentPageFirst)}
            onMouseEnter={e => { if (!isCurrentPageFirst) (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
            onMouseLeave={e => { if (!isCurrentPageFirst) (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageNumbers}

          <button
            onClick={() => changePage(currentPage + 1)}
            disabled={isCurrentPageLast}
            style={navBtnStyle(isCurrentPageLast)}
            onMouseEnter={e => { if (!isCurrentPageLast) (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
            onMouseLeave={e => { if (!isCurrentPageLast) (e.currentTarget as HTMLButtonElement).style.background = '#fff'; }}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>Rows per page</span>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            const nextItemsPerPage = parseInt(e.target.value, 10);
            if (resetPageOnItemsPerPageChange && currentPage !== 1) {
              setCurrentPage(1);
            }
            setItemsPerPage(nextItemsPerPage);
          }}
          style={{
            height: 34,
            padding: '0 28px 0 10px',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#fff',
            fontSize: 13,
            color: '#374151',
            cursor: 'pointer',
            outline: 'none',
            appearance: 'auto',
          }}
          aria-label="Records per page"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={150}>150</option>
          <option value={200}>200</option>
        </select>
      </div>
    </div>
  );
};

export default TablePaginationComponent;
